import kebabCase from 'lodash/kebabCase';
import uniqueId from 'lodash/uniqueId';
import type {
  FieldValues,
  ControllerProps,
  FieldPath,
  PathValue,
} from 'react-hook-form';
import { Controller } from 'react-hook-form';

import {
  Autocomplete,
  type AutocompleteProps,
} from '@packages/fenrir/components/autocomplete';
import { FormControlWrapper } from '@packages/fenrir/components/form-control-wrapper';
import {
  OutlinedInput,
  type OutlinedInputProps,
} from '@packages/fenrir/components/outlined-input';

export type Option = {
  label: string;
  value: string;
};

export type ComboBoxFreeSoloValueMapping<FreeSolo> = FreeSolo extends true
  ? string
  : never;

export type ValueTransform<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  FreeSolo extends boolean | undefined = false,
> = {
  input: (
    value: PathValue<TFieldValues, TName>,
  ) => ComboBoxFreeSoloValueMapping<FreeSolo> | Option | null;
  output: (
    value: ComboBoxFreeSoloValueMapping<FreeSolo> | Option | null,
  ) => PathValue<TFieldValues, TName>;
};

export type ControlledComboboxV2Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  FreeSolo extends boolean | undefined = false,
> = {
  controllerProps: Omit<
    ControllerProps<TFieldValues, TName>,
    'render' | 'children'
  >;
  autoCompleteProps: Omit<
    AutocompleteProps<Option, false, boolean, FreeSolo>,
    | 'multiple'
    | 'input'
    | 'onChange'
    | 'renderInput'
    | 'value'
    | 'defaultValue'
    | 'onBlur'
  >;
  transform?: ValueTransform<TFieldValues, TName, FreeSolo>;
  label: string;
  outlinedInputProps?: Omit<OutlinedInputProps, 'inputProps' | 'defaultValue'>;
  retainOnBlur?: boolean;
} & Omit<
  React.ComponentProps<typeof FormControlWrapper>,
  'error' | 'children' | 'fullWidth' | 'defaultValue' | 'inputId' | 'label'
>;

export const ControlledComboboxV2 = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  FreeSolo extends boolean | undefined = false,
>({
  controllerProps,
  label,
  helperText,
  disabled,
  autoCompleteProps,
  outlinedInputProps,
  transform,
  retainOnBlur,
  ...formControlWrapperProps
}: ControlledComboboxV2Props<TFieldValues, TName, FreeSolo>) => {
  const inputId = uniqueId(kebabCase(label)); // TODO: Move util to fenrir

  return (
    <Controller<TFieldValues, TName>
      {...controllerProps}
      render={({ field, fieldState }) => (
        <Autocomplete
          disabled={disabled}
          {...autoCompleteProps}
          {...field}
          onChange={(_, value) =>
            field.onChange(transform?.output(value) ?? value)
          }
          value={transform ? transform.input(field.value) : field.value}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderInput={({ InputProps, inputProps }) => (
            <FormControlWrapper
              inputId={inputId}
              label={label}
              helperText={fieldState.error?.message ?? helperText}
              error={Boolean(fieldState.error)}
              disabled={disabled}
              fullWidth
              {...formControlWrapperProps}
            >
              <OutlinedInput
                {...InputProps}
                {...outlinedInputProps}
                inputProps={{
                  ...inputProps,
                  id: inputId,
                  'aria-labelledby': `${inputId}-label`,
                  'aria-describedby': helperText
                    ? `${inputId}-helper-text`
                    : undefined,
                }}
                onBlur={(e) => {
                  outlinedInputProps?.onBlur?.(e);
                  if (retainOnBlur) {
                    const { value } = e.target;
                    field.onChange(value);
                  }
                }}
              />
            </FormControlWrapper>
          )}
        />
      )}
    />
  );
};
